import countries from 'i18n-iso-countries';
import isEmail from 'validator/lib/isEmail';
import { REGIONS_BY_COUNTRY } from './constants';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

export const COMPANY_NAME_MAX_LENGTH = 83;

const COUNTRY_ALIASES: Record<string, string> = {
  US: 'US',
  USA: 'US',
  'UNITED STATES': 'US',
};

const INVALID_PHONE_PLACEHOLDERS = /^(0+|na)$/i;
const OPTIONAL_PHONE_MIN_LENGTH = 7;
const OPTIONAL_PHONE_MAX_LENGTH = 30;
const MAX_PHONE_INPUT_LENGTH = 50;

const companyNamePattern = (maxLength: number) => new RegExp(`^[a-z0-9 ,.&'\\-:+/\\\\_[\\]|~]{2,${maxLength}}$`, 'i');

const getStateValidationPattern = (country = 'United States of America') =>
  new RegExp(`^(${(REGIONS_BY_COUNTRY[country] || []).join('|')})$`, 'i');

export const normalizeCountryCode = (value?: string | null): string | null => {
  if (!value?.trim()) return null;

  const trimmed = value.trim();
  const alias = COUNTRY_ALIASES[trimmed.toUpperCase()];
  if (alias) return alias;

  if (/^[A-Za-z]{2}$/.test(trimmed) && countries.isValid(trimmed.toUpperCase())) {
    return trimmed.toUpperCase();
  }

  const alpha2 = countries.getAlpha2Code(trimmed, 'en');
  return alpha2 ?? null;
};

export const isValidCountryCode = (value?: string | null): boolean => normalizeCountryCode(value) != null;

const isPhoneDigit = (char: string) => char >= '0' && char <= '9';

const findTrailingDigitStart = (value: string): number => {
  let digitStart = value.length;
  while (digitStart > 0 && isPhoneDigit(value[digitStart - 1])) {
    digitStart--;
  }
  return digitStart;
};

const findMarkerEndBeforeDigits = (value: string, digitStart: number): number => {
  let markerEnd = digitStart;
  while (markerEnd > 0 && value[markerEnd - 1] === ' ') {
    markerEnd--;
  }
  return markerEnd;
};

const getPhoneExtensionCutIndex = (value: string, lower: string, markerEnd: number): number | null => {
  if (markerEnd >= 4 && lower.slice(markerEnd - 4, markerEnd) === 'ext.') {
    return markerEnd - 4;
  }
  if (markerEnd >= 3 && lower.slice(markerEnd - 3, markerEnd) === 'ext') {
    return markerEnd - 3;
  }
  if (markerEnd >= 1 && lower[markerEnd - 1] === 'x') {
    const xIndex = markerEnd - 1;
    const charBeforeX = value[xIndex - 1];
    if (xIndex > 0 && (charBeforeX === ' ' || isPhoneDigit(charBeforeX))) {
      return charBeforeX === ' ' ? xIndex - 1 : xIndex;
    }
  }
  return null;
};

const stripPhoneExtension = (value: string): string => {
  const s = value.trim().slice(0, MAX_PHONE_INPUT_LENGTH);
  if (s.length === 0) return s;

  const digitStart = findTrailingDigitStart(s);
  if (digitStart === s.length) return s;

  const cutIndex = getPhoneExtensionCutIndex(s, s.toLowerCase(), findMarkerEndBeforeDigits(s, digitStart));
  return cutIndex == null ? s : s.slice(0, cutIndex).trimEnd();
};

const getPhoneDigits = (value: string): string => stripPhoneExtension(value.trim()).replace(/\D/g, '');

const hasInvalidPhoneLetters = (value: string): boolean => /[a-zA-Z]/.test(stripPhoneExtension(value.trim()));

export const isValidOptionalPhone = (value?: string | null): boolean => {
  if (!value?.trim()) return true;

  const trimmed = value.trim();
  if (trimmed.length > MAX_PHONE_INPUT_LENGTH) return false;
  if (hasInvalidPhoneLetters(trimmed)) return false;

  const digits = getPhoneDigits(trimmed);
  if (!digits) return false;
  if (INVALID_PHONE_PLACEHOLDERS.test(digits)) return false;

  return digits.length >= OPTIONAL_PHONE_MIN_LENGTH && digits.length <= OPTIONAL_PHONE_MAX_LENGTH;
};

export const getFormFieldValidations = (country: string | null) => {
  return {
    companyName: companyNamePattern(COMPANY_NAME_MAX_LENGTH),
    firstName: /^[a-z ,.'-]*$/i,
    lastName: /^[a-z ,.'-]*$/i,
    street1: /^[a-z0-9 ,.'#_[|~/\-\\\]]{2,}$/i,
    street2: /^[a-z0-9 ,.'#_[|~/\-\\\]]*$/i,
    city: /^[a-z0-9 ,.'-]{2,}$/i,
    state: getStateValidationPattern(country == null ? 'United States of America' : country),
    postalCode:
      country === 'CA' || country === 'Canada' ? /[A-Za-z]\d[A-Za-z]\s?-?\d[A-Za-z]\d/ : /^\d{5}(?:[-\s]\d{4})?$/,
    password: /^.{1,50}$/i,
  };
};

export const isEmailValid = (email?: string) => !!email && isEmail(email) && email.length <= 50;

const { companyName: companyNameValidationRegex } = getFormFieldValidations('');

const streetValidCharactersMessage =
  'Addresses can include letters, numbers, spaces, and the following symbols: ' + ", . ' - # _ [ | ~ / \\ ]";

const companyNameInvalidCharactersMessage =
  'Company names can include letters, numbers, spaces, and the following symbols: ' + ", . & ' - : + / \\ _ [ ] | ~";

const getCompanyNameErrorMessage = (value: string): string => {
  if (value.length < 2) return 'Company name is invalid';
  if (value.length > COMPANY_NAME_MAX_LENGTH) {
    return `Company name must be fewer than ${COMPANY_NAME_MAX_LENGTH} characters`;
  }
  if (!value.match(companyNameValidationRegex)) return companyNameInvalidCharactersMessage;

  return 'Company name is invalid';
};

const getAddressFieldErrorMessage = (value: string): string => {
  if (value.length < 2) return 'Address is invalid';
  if (value.length > 50) return 'Address must be fewer than 50 characters';

  return streetValidCharactersMessage;
};

const getEmailErrorMessage = (value: string): string => {
  if (value.match(/(\.\.)/)) return 'Email should not include consecutive periods';
  if (!value.match('@')) return 'Email should include an @ sign';

  const emailParts = value.split('@');
  if (emailParts.length < 3 && !emailParts[1].match(/\./)) return 'Email should include a dot';
  if (emailParts.length < 3 && emailParts[1].split('.')[1].length < 2) return 'Please complete your email address';
  if (value.length > 50) return 'Email address is too long. Try an email shorter than 50 characters.';

  return 'Email is invalid';
};

const getPhoneErrorMessage = (value: string): string => {
  const trimmed = value.trim();
  const digits = getPhoneDigits(trimmed);

  if (/^na$/i.test(trimmed) || (digits && INVALID_PHONE_PLACEHOLDERS.test(digits))) {
    return 'Please enter a valid phone number';
  }
  if (hasInvalidPhoneLetters(trimmed)) {
    return 'Phone number is invalid';
  }
  if (!digits || digits.length < OPTIONAL_PHONE_MIN_LENGTH || digits.length > OPTIONAL_PHONE_MAX_LENGTH) {
    return 'Phone number is invalid';
  }

  return 'Phone number is invalid';
};

export const getFieldErrorMessage = (field: string, value: string | null) => {
  if (value === '' || value == null) return 'This field is required';

  switch (field) {
    case 'companyName':
      return getCompanyNameErrorMessage(value);
    case 'city':
    case 'street1':
      return getAddressFieldErrorMessage(value);
    case 'email':
      return getEmailErrorMessage(value);
    case 'phone':
      return getPhoneErrorMessage(value);
    case 'country':
      return 'Please select a valid country';
    case 'street2':
      return streetValidCharactersMessage;
    case 'password':
      return 'Try a more complex password';
    default:
      return 'Input is invalid';
  }
};
